<template>
  <div
    v-if="hasTour && !isMobile && !isReactNative"
    class="list-icon_item dropdown">
    <RoundDoubleIconButton
      icon-inner="book-open-reader"
      icon-outer="info"
      :outlined="false"
      data-bs-toggle="dropdown"
      data-test="open-guided-tour" />
    <div class="dropdown-menu py-0 rounded tw-w-64">
      <div class="tw-bg-primary-500 tw-text-primary-contrast tw-flex tw-px-2 tw-py-4">
        <div
          class="tw-font-bold tw-m-auto tw-items-center tw-text-break"
          v-html="t('header.label_guided_tour')"></div>
      </div>
      <template
        v-for="(tours, category) in toursByCategory"
        :key="category">
        <div>
          <div class="tw-bg-primary-300 tw-text-primary-contrast tw-d-flex tw-px-2 tw-py-2">
            <div class="font-bold m-auto text-break">
              {{ category }}
            </div>
          </div>
          <li
            v-for="tour in tours"
            :key="tour.guidedTourUiId">
            <a
              href="#"
              class="tw-flex tw-items-center tw-shadow tw-p-2 tw-cursor-pointer hover:tw-bg-primary-50 tw-text-primary-600"
              @click.prevent="displayTour(tour.section, tour.guidedTourUiId, tour.currentStep, tour.isComplete)"
              :data-test="'tour-selector-' + tour.guidedTourUiId">
              <span>
                {{ t(tour.name) }}
              </span>
              <div class="tw-flex tw-justify-end tw-ml-auto">
                <div v-if="tour.isComplete">
                  <RoundIconButton
                    fa-icon="flag-checkered"
                    @click="displayTour(tour.section, tour.guidedTourUiId, FIRST_TOUR_STEP, tour.isComplete)"
                    :outlined="false" />
                </div>
                <div v-else>
                  <RoundStepBar
                    :currentStep="tour.currentStep"
                    :totalSteps="tour.totalSteps" />
                </div>
              </div>
            </a>
          </li>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import RoundStepBar from "@/components/library/RoundBar/RoundStepBar.vue";
import RoundDoubleIconButton from "@/components/library/RoundDoubleIconButton/RoundDoubleIconButton.vue";
import RoundIconButton from "@/components/library/RoundIconButton/RoundIconButton.vue";
import { useAppWrapper } from "@/composables/useAppWrapper";
import { useIsMobile } from "@/composables/useIsMobile";
import { FIRST_TOUR_STEP, RESET_TOUR_STEP, TOUR_ID, TOUR_CATEGORIES } from "@/constants/tour";
import { useDeviceStore } from "@/storeModules/devices/DeviceStore";
import { useGuidedTourStore } from "@/storeModules/guidedtours/GuidedTourStore";
import { useUserConfigStore } from "@/storeModules/userConfigs";
import { DXToursByCategory } from "@/types";
import { useTranslation } from "i18next-vue";
import { computed, ref } from "vue";

const deviceStore = useDeviceStore();
const guidedTourStore = useGuidedTourStore();
const userStore = useUserConfigStore();
const { isMobile } = useIsMobile();
const { isReactNative } = useAppWrapper();
const { t } = useTranslation();

const openTour = ref(false);

const toursByCategory = computed(() => {
  const categories = guidedTourStore.completedOrSavedToursByCategory;
  const sortedCategories: DXToursByCategory = {};

  for (const category in categories) {
    sortedCategories[category] = [...categories[category]].sort((a, b) => a.rank - b.rank);
  }

  const hasVirtualDeviceInAccount = deviceStore.getDevices.some((device) => device.type.startsWith("virtual"));

  // if the current user does not have any virtual devices but has "Math Functionality" tours listed
  // remove everything from category except the "Virtual Device Tour".
  if (!hasVirtualDeviceInAccount && sortedCategories[TOUR_CATEGORIES.MATH_FUNCTIONALITY]?.length) {
    sortedCategories[TOUR_CATEGORIES.MATH_FUNCTIONALITY] = sortedCategories[TOUR_CATEGORIES.MATH_FUNCTIONALITY].filter(
      (tour) => tour.guidedTourUiId === TOUR_ID.VIRTUAL_DEVICE_TOUR
    );

    // if the category is empty after filtering, remove it from the list.
    if (!sortedCategories[TOUR_CATEGORIES.MATH_FUNCTIONALITY].length) {
      delete sortedCategories[TOUR_CATEGORIES.MATH_FUNCTIONALITY];
    }
  }
  return sortedCategories;
});

const hasTour = computed(() => Object.values(toursByCategory.value).some((category) => category.length > 0));

async function displayTour(category: string, tourId: string, currentStep: number, isTourComplete: boolean) {
  openTour.value = false;
  const tourToBeDisplayed = await guidedTourStore.getTourByIdAndCategory(category, tourId);

  if (userStore.hasMathFunctionality && tourToBeDisplayed) {
    tourToBeDisplayed.currentStep = currentStep;
    const targetStep = isTourComplete ? RESET_TOUR_STEP : currentStep - 2;
    await guidedTourStore.loadTour(tourToBeDisplayed, targetStep);
    await guidedTourStore.incrementStep();
    openTour.value = true;
  }
}
</script>